import envStore from '@shared/env'
import Button from '@shared/Form/Button'
import { Props } from '@shared/Form/Button/Button'
import { message, Upload, UploadProps } from 'antd'
import { isNil, path, pathOr } from 'ramda'
import React from 'react'
import Store from 'store'
import styled, { css } from 'styled-components'

export interface ImageInfo {
  hash: string
  previewUrl: string
  path: string
}

interface ImageUploaderButtonProps extends Omit<UploadProps<any>, 'onChange'> {
  value?: string | ImageInfo
  onChange?: (imageInfo?: any) => void
  children?: React.ReactNode
  buttonProps?: Omit<Props, 'children'>
  uploadOwner?: string
  hiddenFileListName?: boolean
  changeValueProp?: string
  readonly?: boolean
}

export interface ImageUploaderButtonRef {
  removeAll: () => void
}

export default React.forwardRef<
  ImageUploaderButtonRef,
  ImageUploaderButtonProps
>((props, ref) => {
  const {
    value,
    onChange,
    children,
    buttonProps,
    uploadOwner,
    hiddenFileListName,
    changeValueProp,
    defaultFileList,
    readonly,
    showUploadList,
    ...others
  } = props

  const [hasChanged, setHasChanged] = React.useState(false)
  const [renderKey, setRenderKey] = React.useState(0)

  React.useEffect(() => {
    setRenderKey(prev => prev + 1)
  }, [defaultFileList])

  React.useEffect(() => {
    if (readonly) {
      setHasChanged(false)
    }
  }, [readonly])

  React.useImperativeHandle(ref, () => ({
    removeAll: () => onChange && onChange(undefined),
  }))

  return (
    <>
      {!readonly && (
        <UploadStyled
          accept="image/*"
          ref={ref}
          multiple={false}
          hiddenFileListName={hiddenFileListName}
          onPreview={file => {
            const image_window = window.open('', '_blank')
            image_window?.document.write(`
          <html>
            <head>
            </head>
            <body>
              <img src="${pathOr(
                '',
                ['response', 'data', 'previewUrl'],
                file
              )}" alt="img" height="100%" width="100%">
            </body>
          </html>
        `)
          }}
          action={`${envStore.apiBaseUrl}/upload/${uploadOwner}/image`}
          headers={{
            Authorization: `Bearer ${Store.get('sctc_token')}`,
            'X-Requested-With': null as any,
          }}
          listType="picture"
          maxCount={1}
          onRemove={f => {
            onChange?.(null)
          }}
          onChange={({ fileList }) => {
            if (fileList.length === 0) {
              onChange?.(null)
              setHasChanged(true)
            } else if (path([0, 'status'], fileList) === 'done') {
              setHasChanged(true)
              onChange?.(
                pathOr(
                  undefined,
                  isNil(changeValueProp)
                    ? [0, 'response', 'data']
                    : [0, 'response', 'data', changeValueProp],
                  fileList
                )
              )
            } else if (path([0, 'status'], fileList) === 'error') {
              message.error(path([0], fileList))
            }
          }}
          showUploadList={showUploadList}
          {...others}>
          <Button {...buttonProps}> {children}</Button>
        </UploadStyled>
      )}
      {!hasChanged && (
        <UploadStyled
          disabled={readonly}
          key={renderKey}
          hiddenFileListName={hiddenFileListName}
          listType="picture"
          maxCount={1}
          onRemove={f => {
            return false
          }}
          showUploadList={showUploadList}
          defaultFileList={defaultFileList}></UploadStyled>
      )}
    </>
  )
})

const UploadStyled = styled(Upload)<{ hiddenFileListName?: boolean }>`
  ${p =>
    p.hiddenFileListName
      ? css`
          .ant-upload-span {
            justify-content: center;
            .ant-upload-list-item-name {
              display: none;
            }
          }
        `
      : ''}

  .ant-upload-list-item-card-actions-btn {
    > .anticon-delete {
      color: #fff;
    }
  }
`
